import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { SAVING_ARENA_API_CONFIG } from './api.config';
import { SlugPrefetchStore } from './slug-prefetch.store';
import {
  FrontendComparison,
  FrontendComparisonPoll,
  FrontendComparisonSummary,
  ApiQueryParams,
  ApiResponse,
  FrontendAuthor,
  FrontendBlog,
  FrontendDealsStore,
  FrontendDealCategoryListItem,
  FrontendDealEventListItem,
  FrontendDealsCategory,
  FrontendDealsEvent,
  FrontendDealsStoreListItem,
  FrontendDealsFeaturedHero,
  FrontendDealsStoreRelated,
  FrontendFeaturedEventStores,
  FrontendTopDiscountStore,
  FrontendBlogCategory,
  FrontendBlogComment,
  FrontendBlogSilo,
  FrontendBlogTag,
  FrontendAboutPage,
  FrontendContactPage,
  FrontendPopularCategory,
  FrontendReviewsSummary,
  FrontendBlogCategoriesPage,
  FrontendStoreSeoSettings,
  FrontendBlogsPage,
  FrontendCategoriesPage,
  FrontendCollectionStore,
  FrontendCoupon,
  FrontendDiscountHub,
  FrontendDiscountPage,
  FrontendCatalogueTotals,
  FrontendDiscountType,
  FrontendStoreDiscountPageLink,
  FrontendCouponBadgeDetail,
  FrontendCouponBadgeSummary,
  FrontendCouponCategory,
  FrontendDealsCategoryProducts,
  FrontendDealsSettings,
  FrontendDealsTopPicks,
  FrontendEvent,
  FrontendHomeSettings,
  FrontendNewCollection,
  FrontendTodayTopDeals,
  FrontendTrendingCoupons,
  FrontendProduct,
  FrontendAssistantAnswer,
  FrontendAssistantContext,
  FrontendSearchResults,
  FrontendShopSettings,
  FrontendSlugResolution,
  FrontendStore,
  FrontendStoreAlphabetSection,
  FrontendUser,
  ListQueryParams,
  PaginatedApiResponse,
} from './api.models';

@Injectable({ providedIn: 'root' })
export class SavingArenaApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SAVING_ARENA_API_CONFIG);
  private readonly prefetch = inject(SlugPrefetchStore);
  private couponCategoriesRequest$?: Observable<ApiResponse<FrontendCouponCategory[]>>;
  private shopSettingsRequest$?: Observable<ApiResponse<FrontendShopSettings>>;

  /**
   * What a bare one-segment URL is, plus the whole payload for it.
   *
   * `/zulily-coupon-codes` and `/1-oz-silver-maples-monster-box-500-pc` are the same shape, and the
   * router cannot tell a store slug from a product slug: a route matcher is synchronous, and with
   * ~38,000 stores and a product catalogue that grows with every feed there is no registry small
   * enough to ship to the browser. The server decides, and returns the page's data in the same
   * response so the page still costs one request.
   */
  resolveSlug(slug: string): Observable<ApiResponse<FrontendSlugResolution>> {
    return this.get(`/resolve/${encodeURIComponent(slug)}`);
  }

  getHome(): Observable<ApiResponse<FrontendHomeSettings | null>> {
    return this.get('/home');
  }

  getDeals(): Observable<ApiResponse<FrontendDealsSettings | null>> {
    return this.get('/deals');
  }

  /**
   * The admin-chosen category row on `/collection`. Separate from `getDeals()` because that call is
   * skipped once the hero slides come out of transfer state.
   */
  getDealsCategoryProducts(): Observable<ApiResponse<FrontendDealsCategoryProducts>> {
    return this.get('/deals/category-products');
  }

  getStores(params?: ListQueryParams): Observable<PaginatedApiResponse<FrontendStore>> {
    return this.getList('/stores', params);
  }

  getStoreAlphabetSections(limit: number): Observable<ApiResponse<FrontendStoreAlphabetSection[]>> {
    return this.get('/stores/alphabet-sections', { limit });
  }

  /**
   * A store by slug — served from the root-slug resolver's payload when that is what brought us
   * here, so reaching a store at `/<slug>` costs the same one request as reaching it at its own URL.
   * StorePage is unchanged either way: it asks for the store and gets it.
   */
  getStore(slug: string): Observable<ApiResponse<FrontendStore>> {
    const prefetched = this.prefetch.takeStore(slug);
    if (prefetched) return of({ success: true, data: prefetched } as ApiResponse<FrontendStore>);
    return this.get(`/stores/${encodeURIComponent(slug)}`);
  }

  /** Submit a 1–5 star rating for a store (one per IP, enforced server-side). */
  // --- Store comparisons -------------------------------------------------------------------------

  /** Published comparisons. `storeSlug` filters to comparisons featuring that store. */
  getComparisons(
    storeSlug?: string,
    limit = 20,
  ): Observable<ApiResponse<FrontendComparisonSummary[]>> {
    return this.get<FrontendComparisonSummary[]>('/comparisons', { store: storeSlug, limit });
  }

  getComparison(slug: string): Observable<ApiResponse<FrontendComparison>> {
    return this.get<FrontendComparison>(`/comparisons/${encodeURIComponent(slug)}`);
  }

  submitComparisonVote(
    slug: string,
    storeSlug: string,
  ): Observable<ApiResponse<FrontendComparisonPoll>> {
    return this.http.post<ApiResponse<FrontendComparisonPoll>>(
      this.url(`/comparisons/${encodeURIComponent(slug)}/votes`),
      { store: storeSlug },
      { withCredentials: false },
    );
  }

  /**
   * The caller's own vote (IP-scoped). Browser-only — see `getStoreRatingState` for why an IP-scoped
   * value must never be resolved during SSR.
   */
  getComparisonVoteState(
    slug: string,
  ): Observable<ApiResponse<{ hasVoted: boolean; votedStoreId: string }>> {
    return this.get<{ hasVoted: boolean; votedStoreId: string }>(
      `/comparisons/${encodeURIComponent(slug)}/vote-state`,
    );
  }

  /**
   * The CALLER's own rating state for a store (IP-scoped).
   *
   * Call this from the browser only. `getStore()` also returns `hasRated`, but during SSR that
   * request comes from the SSR server, so its value describes the server rather than the visitor —
   * and the rendered HTML is cached and shared. See the endpoint's comment in the backend.
   */
  getStoreRatingState(
    slug: string,
  ): Observable<ApiResponse<{ rating: number; ratingCount: number; hasRated: boolean }>> {
    return this.get<{ rating: number; ratingCount: number; hasRated: boolean }>(
      `/stores/${encodeURIComponent(slug)}/rating-state`,
    );
  }

  submitStoreRating(
    slug: string,
    stars: number,
  ): Observable<ApiResponse<{ rating: number; ratingCount: number; hasRated: boolean }>> {
    return this.http.post<ApiResponse<{ rating: number; ratingCount: number; hasRated: boolean }>>(
      this.url(`/stores/${encodeURIComponent(slug)}/ratings`),
      { stars },
      { withCredentials: false },
    );
  }

  /** Follow a store by email (one per email per store, enforced server-side). */
  followStore(
    slug: string,
    email: string,
  ): Observable<ApiResponse<{ following: boolean; followers?: number }>> {
    return this.http.post<ApiResponse<{ following: boolean; followers?: number }>>(
      this.url(`/stores/${encodeURIComponent(slug)}/followers`),
      { email },
      { withCredentials: false },
    );
  }

  /** Submit a store review. Saved pending admin approval before it appears publicly. */
  submitStoreReview(
    slug: string,
    payload: {
      userName: string;
      userDesignation?: string;
      title: string;
      description: string;
      rating: number;
    },
  ): Observable<ApiResponse<{ pending: boolean }>> {
    return this.http.post<ApiResponse<{ pending: boolean }>>(
      this.url(`/stores/${encodeURIComponent(slug)}/reviews`),
      payload,
      { withCredentials: false },
    );
  }

  /** Submit a coupon for a store. Saved pending admin approval before it appears publicly. */
  submitStoreCoupon(
    slug: string,
    payload: { title: string; code?: string; description: string; endDateText?: string },
  ): Observable<ApiResponse<{ pending: boolean }>> {
    return this.http.post<ApiResponse<{ pending: boolean }>>(
      this.url(`/stores/${encodeURIComponent(slug)}/coupons`),
      payload,
      { withCredentials: false },
    );
  }

  getCoupons(params?: ListQueryParams): Observable<PaginatedApiResponse<FrontendCoupon>> {
    return this.getList('/coupons', params);
  }

  /**
   * A store's Student / Teacher / Military discount page by its generated slug
   * (`auxbeam-student-discount`). 404s for a draft page, exactly as the storefront does.
   */
  getDiscountPage(slug: string): Observable<ApiResponse<FrontendDiscountPage>> {
    return this.get(`/discount-pages/${encodeURIComponent(slug)}`);
  }

  /**
   * The admin-managed discount categories. Routing uses these via the app initializer in
   * `discount-type-registry`; call this directly only to list them in the UI.
   */
  getDiscountTypes(): Observable<ApiResponse<FrontendDiscountType[]>> {
    return this.get('/discount-types');
  }

  /**
   * How much of the catalogue is live: active stores, coupons and products.
   *
   * Counts come from the same `status: 'active'` test the public listings use, so a headline figure
   * can never promise more than the pages below it will show. The backend holds them for a minute.
   */
  getCatalogueTotals(): Observable<ApiResponse<FrontendCatalogueTotals>> {
    return this.get('/catalogue-totals');
  }

  /**
   * A store's published discount pages, so its coupon page can link to every one of them.
   * Returns `[]` for an unknown store rather than erroring.
   */
  getStoreDiscountPages(
    storeSlug: string,
  ): Observable<ApiResponse<FrontendStoreDiscountPageLink[]>> {
    return this.get(`/stores/${encodeURIComponent(storeSlug)}/discount-pages`);
  }

  /** One discount type's hub page: every store with a published page of that type. */
  getDiscountHub(slugSuffix: string): Observable<ApiResponse<FrontendDiscountHub>> {
    return this.get(`/discount-types/${encodeURIComponent(slugSuffix)}`);
  }

  /**
   * Records one coupon action click (Show Code / Copy Code / Get Deal) against the admin panel's
   * Hits counter. Call it only from a real click handler — never on render, and never from a
   * resolver or effect — or the count stops meaning "someone acted on this offer".
   *
   * Clicks that leave through the `/go/:store/:id` redirect are already counted server-side
   * by that redirect, so they must not call this as well or one click becomes two hits.
   */
  recordCouponHit(id: string): Observable<ApiResponse<{ id: string; hits: number }>> {
    return this.http.post<ApiResponse<{ id: string; hits: number }>>(
      this.url(`/coupons/${encodeURIComponent(id)}/hit`),
      {},
      { withCredentials: false },
    );
  }

  getCoupon(id: string): Observable<ApiResponse<FrontendCoupon>> {
    return this.get(`/coupons/${encodeURIComponent(id)}`);
  }

  /** Stores that have coupons in a switch collection (Verified / Exclusive / …), most first. */
  getCouponCollectionStores(
    params?: ListQueryParams,
  ): Observable<ApiResponse<FrontendCollectionStore[]>> {
    return this.get('/coupons/stores', params);
  }

  /** All active coupon badges (system + custom) for the `/coupons` hub. */
  getCouponBadges(): Observable<ApiResponse<FrontendCouponBadgeSummary[]>> {
    return this.get('/coupon-badges');
  }

  /** One coupon badge by slug, with full SEO + content, for its `/coupons/<slug>` collection page. */
  getCouponBadge(slug: string): Observable<ApiResponse<FrontendCouponBadgeDetail>> {
    return this.get(`/coupon-badges/${encodeURIComponent(slug)}`);
  }

  getCouponCategories(): Observable<ApiResponse<FrontendCouponCategory[]>> {
    this.couponCategoriesRequest$ ??= this.get<FrontendCouponCategory[]>('/coupon-categories').pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.couponCategoriesRequest$;
  }

  getCouponCategory(id: string): Observable<ApiResponse<FrontendCouponCategory>> {
    return this.get(`/coupon-categories/${encodeURIComponent(id)}`);
  }

  /**
   * Admin-managed settings for the storefront "/categories" page (SEO, hero copy, FAQs,
   * long description and featured blogs). `data` is null until the admin saves it once.
   */
  getCategoriesPageSettings(): Observable<ApiResponse<FrontendCategoriesPage | null>> {
    return this.get('/pages/categories');
  }

  /**
   * Admin-managed settings for the storefront "/blogs" page (SEO, hero copy, popular blog
   * categories, guides section and FAQs). `data` is null until the admin saves it once.
   */
  getBlogsPageSettings(): Observable<ApiResponse<FrontendBlogsPage | null>> {
    return this.get('/pages/blogs');
  }

  /**
   * Admin-managed settings for the storefront "/blogs/categories" page (SEO, headings,
   * descriptions). `data` is null until the admin saves it once.
   */
  getBlogCategoriesPageSettings(): Observable<ApiResponse<FrontendBlogCategoriesPage | null>> {
    return this.get('/pages/blog-categories');
  }

  /**
   * Admin-managed fallback wording for store pages — the meta title and H1 templates used when a
   * store has none of its own. `data` is null until the admin saves it once.
   */
  getStoreSeoSettings(): Observable<ApiResponse<FrontendStoreSeoSettings | null>> {
    return this.get('/settings/store-seo');
  }

  /**
   * Whether the Shop is live, from the master switch on the admin's Shop Overview page.
   *
   * Cached for the app's lifetime like `getCouponCategories`: it is one boolean, wanted by the header and
   * footer on every page and by the route guard on every `/shop` navigation. During SSR the response
   * is serialised into the document by the HTTP transfer cache, so the browser does not re-request
   * it and the Shop link never flickers in or out after hydration.
   */
  getShopSettings(): Observable<ApiResponse<FrontendShopSettings>> {
    this.shopSettingsRequest$ ??= this.get<FrontendShopSettings>('/settings/shop').pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.shopSettingsRequest$;
  }

  /**
   * Admin-managed settings for the storefront "/about" page (SEO, hero, story content, stats,
   * values). `data` is null until the admin saves it once.
   */
  getAboutPageSettings(): Observable<ApiResponse<FrontendAboutPage | null>> {
    return this.get('/pages/about');
  }

  /**
   * Admin-managed settings for the storefront "/contact" page (SEO, hero, contact channels,
   * enquiry form copy). `data` is null until the admin saves it once.
   */
  getContactPageSettings(): Observable<ApiResponse<FrontendContactPage | null>> {
    return this.get('/pages/contact');
  }

  /**
   * Latest approved shopper reviews across all stores, with the overall rating aggregate.
   * Powers the "social proof" reviews sections. `data` is null only on error.
   */
  getLatestReviews(limit = 9): Observable<ApiResponse<FrontendReviewsSummary | null>> {
    return this.get('/reviews', { limit });
  }

  /**
   * Admin-flagged coupon categories for the home page "Popular Categories" > "Coupons & Offers" tab.
   */
  getPopularCategories(limit = 20): Observable<ApiResponse<FrontendPopularCategory[]>> {
    return this.get('/popular-categories', { limit });
  }

  getEvents(
    params?: Pick<ListQueryParams, 'page' | 'limit' | 'search'>,
  ): Observable<PaginatedApiResponse<FrontendEvent>> {
    return this.getList('/events', params);
  }

  getEvent(id: string): Observable<ApiResponse<FrontendEvent>> {
    return this.get(`/events/${encodeURIComponent(id)}`);
  }

  /**
   * Lightweight variant of getEvent that returns only the event's featured top stores
   * (`data.topStoresFeatured` / `data.topStores`) — no coupons/products/blogs. Used by the home
   * page "Top Stores" section to avoid downloading the full (large) event detail.
   */
  getEventTopStores(id: string): Observable<ApiResponse<FrontendEvent>> {
    return this.get(`/events/${encodeURIComponent(id)}/top-stores`);
  }

  getBlogs(params?: ListQueryParams): Observable<PaginatedApiResponse<FrontendBlog>> {
    return this.getList('/blogs', params);
  }

  getBlog(slug: string): Observable<ApiResponse<FrontendBlog>> {
    return this.get(`/blogs/${encodeURIComponent(slug)}`);
  }

  getBlogCategories(): Observable<ApiResponse<FrontendBlogCategory[]>> {
    return this.get('/blog-categories');
  }

  getBlogCategory(slug: string): Observable<ApiResponse<FrontendBlogCategory>> {
    return this.get(`/blog-categories/${encodeURIComponent(slug)}`);
  }

  /** Silo list for the topic hub. Each entry carries `totalBlogs`, the count of guides it holds. */
  getBlogSilos(params?: ListQueryParams): Observable<PaginatedApiResponse<FrontendBlogSilo>> {
    return this.getList('/blog-silos', params);
  }

  getBlogSilo(slug: string): Observable<ApiResponse<FrontendBlogSilo>> {
    return this.get(`/blog-silos/${encodeURIComponent(slug)}`, { _t: Date.now() });
  }

  getBlogTags(): Observable<ApiResponse<FrontendBlogTag[]>> {
    return this.get('/blog-tags');
  }

  getUsers(params?: ListQueryParams): Observable<PaginatedApiResponse<FrontendUser>> {
    return this.getList('/users', params);
  }

  getUser(slug: string): Observable<ApiResponse<FrontendUser>> {
    return this.get(`/users/${encodeURIComponent(slug)}`);
  }

  getAuthor(slug: string): Observable<ApiResponse<FrontendAuthor>> {
    return this.get(`/authors/${encodeURIComponent(slug)}`);
  }

  getProducts(params?: ListQueryParams): Observable<PaginatedApiResponse<FrontendProduct>> {
    return this.getList('/products', params);
  }

  /** A product by id or slug. Served from the root-slug resolver's payload when it fetched this one. */
  getProduct(id: string): Observable<ApiResponse<FrontendProduct>> {
    const prefetched = this.prefetch.takeProduct(id);
    if (prefetched) return of({ success: true, data: prefetched } as ApiResponse<FrontendProduct>);
    return this.get(`/products/${encodeURIComponent(id)}`);
  }

  /** Newest active product for each store (one product per store), most-recent store first. */
  getLatestProductsPerStore(limit = 8): Observable<ApiResponse<FrontendProduct[]>> {
    return this.get('/products/latest-per-store', { limit });
  }

  /** Each store's deepest markdown, biggest discount first — one product per store. */
  getTopDiscountProductsPerStore(limit = 12): Observable<ApiResponse<FrontendProduct[]>> {
    return this.get('/products/top-discount-per-store', { limit });
  }

  /**
   * The "Shop Our Top Picks" row on `/collection`. Admin-chosen stores when Settings » Deals Page »
   * Top Picks has any, otherwise the automatic biggest-discount ranking — the API decides, so the
   * page just renders what comes back.
   */
  getDealsTopPicks(limit = 12): Observable<ApiResponse<FrontendDealsTopPicks>> {
    return this.get('/deals/top-picks', { limit });
  }

  /** Counts a share of a store page and returns the store's new share total. */
  shareStore(slug: string): Observable<ApiResponse<{ shared: boolean; shares: number }>> {
    return this.http.post<ApiResponse<{ shared: boolean; shares: number }>>(
      this.url(`/stores/${encodeURIComponent(slug)}/shares`),
      {},
      { withCredentials: false },
    );
  }

  /**
   * Products on a deals store page. `nav` is a key from the detail response's `nav` array; omit it
   * for the store's whole catalogue.
   */
  getDealsStoreProducts(
    storeKey: string,
    params: { nav?: string; sort?: string; search?: string; page?: number; limit?: number } = {},
  ): Observable<PaginatedApiResponse<FrontendProduct>> {
    return this.getList(`/deals-stores/${encodeURIComponent(storeKey)}/products`, params);
  }

  /** Store heroes an admin featured on the /deals landing page (one → single hero, many → carousel). */
  getDealsFeaturedHeroes(): Observable<ApiResponse<FrontendDealsFeaturedHero[]>> {
    return this.get('/deals-stores/featured-heroes');
  }

  /** Other deals-page stores sharing a category with this one, for the related-stores carousel. */
  getDealsStoreRelated(
    storeKey: string,
    limit = 12,
  ): Observable<ApiResponse<FrontendDealsStoreRelated[]>> {
    return this.get(`/deals-stores/${encodeURIComponent(storeKey)}/related`, { limit });
  }

  /** Stores that automatically have a deals page, i.e. those carrying enough active products. */
  getDealsStores(limit = 24): Observable<ApiResponse<FrontendDealsStoreListItem[]>> {
    return this.get('/deals-stores', { limit });
  }

  /**
   * The store behind a /deals/:storeName page plus its optional hero banner. `storeKey` is the
   * store's domain without any `*.`/`www.` prefix; slug and id also resolve. 404s when the store
   * has too few products to warrant a page.
   */
  getDealsStore(storeKey: string): Observable<ApiResponse<FrontendDealsStore>> {
    return this.get(`/deals-stores/${encodeURIComponent(storeKey)}`);
  }

  /**
   * Admin-authored content (hero banner, editorial images, long-form guide, FAQs) for a
   * `/collection/:slug` page. 404s when the category doesn't exist or has too few products to
   * warrant a page — the storefront then just keeps its static sections.
   */
  getDealsCategory(slug: string): Observable<ApiResponse<FrontendDealsCategory>> {
    return this.get(`/deal-categories/${encodeURIComponent(slug)}`);
  }

  /**
   * The categories that currently have a `/collection/:slug` page (enough active products to
   * qualify), richest first. The endpoint applies the same qualifying rule as `getDealsCategory`,
   * so every slug here resolves rather than 404ing.
   */
  getDealsCategories(limit = 12): Observable<ApiResponse<FrontendDealCategoryListItem[]>> {
    return this.get('/deal-categories', { limit });
  }

  /**
   * An event's DEALS page (`/deals/event/:slug`) — the product side of an event, as opposed to
   * `getEvent`, which backs its coupon page. 404s when the event's stores carry too few products.
   */
  getDealsEvent(slug: string): Observable<ApiResponse<FrontendDealsEvent>> {
    return this.get(`/deal-events/${encodeURIComponent(slug)}`);
  }

  /**
   * The events that currently have a `/deals/event/:slug` page. Applies the same qualifying rule as
   * `getDealsEvent`, so every slug here resolves rather than 404ing.
   */
  getDealsEvents(limit = 12): Observable<ApiResponse<FrontendDealEventListItem[]>> {
    return this.get('/deal-events', { limit });
  }

  /**
   * Stores assigned to whichever event is featured right now (running, else next upcoming) that also
   * have products. The event rollover is decided server-side so it matches the header's promo link.
   */
  getFeaturedEventStores(limit = 12): Observable<ApiResponse<FrontendFeaturedEventStores>> {
    return this.get('/events/featured/stores', { limit });
  }

  /** Stores ranked by the deepest markdown in their catalogue (logo + link + best discount). */
  getTopDiscountStores(limit = 12): Observable<ApiResponse<FrontendTopDiscountStore[]>> {
    return this.get('/stores/top-discount', { limit });
  }

  /**
   * Home page "New Collection": admin-flagged categories (the tabs) plus the latest per-store
   * products for each, keyed by category id.
   */
  getNewCollection(limit = 8): Observable<ApiResponse<FrontendNewCollection>> {
    return this.get('/new-collection', { limit });
  }

  /**
   * Home page "Trending Coupons": admin-flagged coupon categories (the tabs) plus the latest
   * active coupons for each, keyed by category id.
   */
  getTrendingCoupons(limit = 6): Observable<ApiResponse<FrontendTrendingCoupons>> {
    return this.get('/trending-coupons', { limit });
  }

  /**
   * Home page "Today's Top Deals": admin-flagged stores (the tabs) plus each store's latest
   * products, keyed by store id.
   */
  getTodayTopDeals(limit = 6): Observable<ApiResponse<FrontendTodayTopDeals>> {
    return this.get('/today-top-deals', { limit });
  }

  search(query: string): Observable<ApiResponse<FrontendSearchResults>> {
    return this.get('/search', { q: query });
  }

  /**
   * One turn of the assistant conversation.
   *
   * Separate from `search()` on purpose: that endpoint answers a typeahead with five flat lists,
   * while this one understands the question ("coupons for Adidas", "under $100", "which has the
   * biggest discount?") and answers it in the light of what was asked before. `context` is whatever
   * the previous answer returned — passing it is what makes the conversation multi-turn.
   */
  askAssistant(
    query: string,
    context: FrontendAssistantContext | null,
  ): Observable<ApiResponse<FrontendAssistantAnswer>> {
    return this.http.post<ApiResponse<FrontendAssistantAnswer>>(
      this.url('/assistant'),
      { query, context },
      { withCredentials: false },
    );
  }

  /**
   * "Add your brand on SavingArena" submission. Sends multipart/form-data (text fields + optional
   * logo file), so it bypasses the JSON/query helpers. The one mutating call on the public API.
   */
  submitStoreRequest(payload: FormData): Observable<ApiResponse<{ id: string | null }>> {
    return this.http.post<ApiResponse<{ id: string | null }>>(
      this.url('/brand-requests'),
      payload,
      {
        withCredentials: false,
      },
    );
  }

  /**
   * A message from the `/contact` form. Saved as a Contact Message in the admin panel and emailed to
   * the notification address; nothing about it is published.
   *
   * `companyWebsite` is the honeypot — a hidden field the form never fills. The server accepts a
   * filled one with a plain success and saves nothing, so a bot gets no signal that it was caught.
   */
  submitContactMessage(payload: {
    name: string;
    email: string;
    message: string;
    subject?: string;
    sourcePath?: string;
    companyWebsite?: string;
  }): Observable<ApiResponse<{ submitted: boolean }>> {
    return this.http.post<ApiResponse<{ submitted: boolean }>>(
      this.url('/contact-messages'),
      payload,
      { withCredentials: false },
    );
  }

  /**
   * Newsletter signup. Idempotent by email server-side — resubmitting a known address succeeds with
   * `alreadySubscribed: true` instead of erroring, so callers show one thank-you either way.
   * `source` tags which form was used; see NEWSLETTER_SOURCES in the backend route.
   */
  subscribeToNewsletter(payload: {
    email: string;
    source: string;
    sourcePath?: string;
    companyWebsite?: string;
  }): Observable<ApiResponse<{ subscribed: boolean; alreadySubscribed: boolean }>> {
    return this.http.post<ApiResponse<{ subscribed: boolean; alreadySubscribed: boolean }>>(
      this.url('/newsletter-subscribers'),
      payload,
      { withCredentials: false },
    );
  }

  /**
   * The "Related Blogs" strip for an article. The backend resolves relatedness as a fallback chain
   * — same silo, then same category, then newest — excluding the article itself, so this is one
   * request rather than three plus a de-dupe here.
   */
  getRelatedBlogs(slug: string, limit = 4): Observable<ApiResponse<FrontendBlog[]>> {
    return this.get(`/blogs/${encodeURIComponent(slug)}/related`, { limit });
  }

  /** Approved comments for one article, threaded one level deep (a comment and its replies). */
  getBlogComments(slug: string): Observable<ApiResponse<FrontendBlogComment[]>> {
    return this.get(`/blogs/${encodeURIComponent(slug)}/comments`);
  }

  /**
   * Submits a comment, or a reply when `parentId` is set. Always lands as pending — nothing shows on
   * the article until an admin approves it in Blogs › Comments. The server rejects anything
   * containing a link with 422; the storefront checks first so the visitor hears it sooner.
   */
  submitBlogComment(
    slug: string,
    payload: {
      authorName: string;
      message: string;
      authorEmail?: string;
      parentId?: string;
      companyWebsite?: string;
    },
  ): Observable<ApiResponse<{ submitted: boolean }>> {
    return this.http.post<ApiResponse<{ submitted: boolean }>>(
      this.url(`/blogs/${encodeURIComponent(slug)}/comments`),
      payload,
      { withCredentials: false },
    );
  }

  private get<T>(path: string, params?: ApiQueryParams): Observable<ApiResponse<T>> {
    return this.http.get<ApiResponse<T>>(this.url(path), {
      params: this.toHttpParams(params),
      withCredentials: false,
    });
  }

  private getList<T>(path: string, params?: ApiQueryParams): Observable<PaginatedApiResponse<T>> {
    return this.http.get<PaginatedApiResponse<T>>(this.url(path), {
      params: this.toHttpParams(params),
      withCredentials: false,
    });
  }

  private url(path: string): string {
    return `${this.config.apiBaseUrl}${path}`;
  }

  private toHttpParams(params?: ApiQueryParams): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '') {
        continue;
      }

      httpParams = httpParams.set(key, String(value));
    }

    return httpParams;
  }
}
